import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { IconPin, IconPinFill } from './icons';

// CoachingMessage is the same shape used by ai/useCoaching consumers; import or
// inline the minimal fields we read to avoid a wide dependency.
export type LatestCoachingHeroMessage = {
	id: string;
	role: 'assistant' | 'user';
	text: string;
	pinnedKeyword?: string;
	pinColor?: string;
	/** Parsed "Say this:" suggestion lifted off the AI reply, if present. */
	sayThis?: string;
	/** "Why" reasoning paragraph(s), if parsed. */
	why?: string;
};

export type LatestCoachingHeroProps = {
	message: LatestCoachingHeroMessage | undefined;
	streaming: boolean;
	pinned: boolean;
	onTogglePin: () => void;
	/**
	 * Optional realtime-coaching websocket connection state. When provided,
	 * a small green/red dot is rendered next to the eyebrow with a hover
	 * tooltip describing whether this window is receiving Twilio webhooks.
	 * Omit to suppress the indicator entirely (e.g. in tests, or when
	 * coaching is not active).
	 */
	wsConnected?: boolean;
};

/**
 * Hero card surfacing the latest assistant coaching reply.
 *
 * Body rendering preference order:
 *   1. `sayThis` — render a quoted "Say this:" callout block plus an optional
 *      "Why:" annotation. Mirrors the legacy `LatestCoachingCard` so the
 *      script-extracted experience is consistent.
 *   2. `text` — fall back to the raw markdown via `AIMarkdownResponse`.
 *
 * While the AI is streaming, the eyebrow swaps to "ANALYZING…" with an inline
 * spinner so reps see the "AI is working" state even when their right column
 * is open. The `data-testid='streaming-indicator'` is preserved on the inline
 * spinner so existing tests keep passing.
 */
export const LatestCoachingHero: FC<LatestCoachingHeroProps> = ({
	message,
	streaming,
	pinned,
	onTogglePin,
	wsConnected,
}) => {
	if (!message || message.role !== 'assistant') return null;

	const pinColor = message.pinColor || 'var(--accent)';
	const hasScript = Boolean(message.sayThis && message.sayThis.trim().length > 0);
	const eyebrowLabel = streaming ? 'ANALYZING…' : (message.pinnedKeyword ?? 'LATEST COACHING');

	return (
		<section
			data-pinned={pinned ? 'true' : 'false'}
			className={css(styles.root)}
			style={message.pinColor ? { borderLeftColor: message.pinColor } : undefined}
		>
			<header className={css(styles.header)}>
				<span className={css(styles.eyebrowGroup)}>
					<span className={css(styles.eyebrow)}>{eyebrowLabel}</span>
					{wsConnected !== undefined && (
						<span
							className={css(styles.wsDot, wsConnected ? styles.wsDotConnected : styles.wsDotDisconnected)}
							title={
								wsConnected
									? 'Connected — this window is receiving Twilio webhooks'
									: 'Not connected — this window is not receiving Twilio webhooks'
							}
							aria-label={wsConnected ? 'Twilio connected' : 'Twilio disconnected'}
							data-testid='ws-status-dot'
							data-connected={wsConnected ? 'true' : 'false'}
						/>
					)}
					{streaming && (
						<span className={css(styles.streamingInline)} data-testid='streaming-indicator'>
							<LoadingSpinner type='extra-tiny' />
							<span className={css(styles.streamingLabel)}>Analyzing…</span>
						</span>
					)}
				</span>
				<button
					type='button'
					className={css(styles.pinButton)}
					onClick={onTogglePin}
					aria-label={pinned ? 'Unpin' : 'Pin'}
					aria-pressed={pinned}
					title={pinned ? 'Unpin' : 'Pin'}
				>
					{pinned ? <IconPinFill size={13} color={pinColor} /> : <IconPin size={13} />}
				</button>
			</header>
			{hasScript ? (
				<>
					<div className={css(styles.scriptBlock)}>
						<div className={css(styles.scriptEyebrow)}>
							<svg
								width='12'
								height='12'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							>
								<path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
							</svg>
							Say this
						</div>
						<div className={css(styles.scriptText)}>{`“${message.sayThis}”`}</div>
					</div>
					{message.why ? (
						<div className={css(styles.whyBlock)}>
							<span className={css(styles.infoChip)}>i</span>
							<span>
								<strong className={css(styles.whyLabel)}>Why: </strong>
								{message.why}
							</span>
						</div>
					) : null}
				</>
			) : (
				<div className={css(styles.body)}>
					<AIMarkdownResponse>{message.text}</AIMarkdownResponse>
				</div>
			)}
		</section>
	);
};

const styles = StyleSheet.create({
	root: {
		padding: '16px 20px',
		background: 'var(--bg-elev)',
		borderLeft: '4px solid var(--accent)',
		borderRadius: 8,
		boxShadow: 'var(--shadow-sm)',
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 8,
		gap: 8,
	},
	eyebrowGroup: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 0,
	},
	eyebrow: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: 0.6,
		color: 'var(--ink-muted)',
	},
	wsDot: {
		display: 'inline-block',
		width: 8,
		height: 8,
		borderRadius: '50%',
		flexShrink: 0,
	},
	wsDotConnected: {
		background: '#22C55E',
		boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.18)',
	},
	wsDotDisconnected: {
		background: '#EF4444',
		boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.18)',
	},
	streamingInline: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--accent)',
		letterSpacing: 0.4,
	},
	streamingLabel: {
		display: 'inline-block',
	},
	pinButton: {
		width: 24,
		height: 24,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	body: {
		fontSize: 15,
		lineHeight: 1.5,
		color: 'var(--ink)',
		margin: 0,
	},
	scriptBlock: {
		background: 'rgba(59, 130, 246, 0.1)',
		border: '1px solid rgba(59, 130, 246, 0.25)',
		borderRadius: 10,
		padding: '12px 14px',
	},
	scriptEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 10.5,
		fontWeight: 600,
		letterSpacing: '0.06em',
		color: '#3B82F6',
		textTransform: 'uppercase',
		marginBottom: 8,
		fontFamily: 'var(--font-mono)',
	},
	scriptText: {
		fontSize: 15.5,
		color: 'var(--ink)',
		lineHeight: 1.5,
		fontWeight: 500,
	},
	whyBlock: {
		marginTop: 10,
		display: 'flex',
		gap: 10,
		alignItems: 'flex-start',
		padding: '10px 12px',
		background: 'var(--bg-sunken)',
		borderRadius: 8,
		color: 'var(--ink-muted)',
		fontSize: 12.5,
		lineHeight: 1.5,
	},
	whyLabel: {
		color: 'var(--ink)',
		fontWeight: 600,
	},
	infoChip: {
		width: 18,
		height: 18,
		borderRadius: 4,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		color: 'var(--ink-faint)',
		fontSize: 10,
		fontWeight: 700,
		marginTop: 1,
	},
});
